import apiService from './apiService';
import {
  mapPlanNameToSlug,
  parseDbPlanId,
  sortPlansByOrder,
  filterActivePlans,
  resolvePlanPriceFromDb,
  pickPlanEntitlements,
  isFreeLikePlan,
} from '../utils/planCatalogUtils';

let plansCache = null;
let plansCacheTimestamp = null;
/** In-memory TTL — prices refresh within ~90s without forcing every mount */
// ***** Code Start BH10092026_1716 - Longer client TTL; force only after checkout
const CACHE_TTL = 90 * 1000;
// ***** Code End BH10092026_1716

const transformPlan = (dbPlan) => {
  const planName = dbPlan.name || dbPlan.plan_name;
  const slug = mapPlanNameToSlug(planName);
  const name = (planName || '').toLowerCase();
  const planPrice = resolvePlanPriceFromDb(dbPlan);
  const entitlements = pickPlanEntitlements(dbPlan);

  let costPerToken = null;
  if (dbPlan.cost_per_token !== null && dbPlan.cost_per_token !== undefined) {
    if (dbPlan.cost_per_token_raw) {
      costPerToken = dbPlan.cost_per_token_raw;
    } else {
      costPerToken = String(dbPlan.cost_per_token);
    }
  }

  const dbId = parseDbPlanId(dbPlan.db_id ?? dbPlan.id);
  const freeLike = isFreeLikePlan(planName, dbPlan.plan_type, false);
  // ***** Code Start BH25082026_1325 - Pass through DB entitlements; never invent trial_days
  const trialDays = entitlements.trial_days;

  return {
    id: slug,
    slug,
    db_id: dbId,
    name: planName,
    price: planPrice,
    yearly_price:
      dbPlan.yearly_price != null ? parseFloat(dbPlan.yearly_price) : null,
    plan_sort: dbPlan.plan_sort != null ? Number(dbPlan.plan_sort) : 999,
    plan_type: dbPlan.plan_type || null,
    cost_per_token: costPerToken,
    cost_per_token_numeric: dbPlan.cost_per_token ? parseFloat(dbPlan.cost_per_token) : null,
    interval:
      name.includes('pay') && (name.includes('go') || name.includes('you'))
        ? 'per 1,000 tokens'
        : freeLike
          ? 'trial'
          : 'month',
    monthly_limit: dbPlan.monthly_limit ?? null,
    trial_days: trialDays,
    ...entitlements,
    features: Array.isArray(dbPlan.features)
      ? dbPlan.features
      : typeof dbPlan.features === 'string'
        ? dbPlan.features.split(',').map((f) => f.trim()).filter(Boolean)
        : [],
    description: dbPlan.description || null,
    is_active: dbPlan.is_active !== false,
  };
  // ***** Code End BH25082026_1325
};

const buildFromResponse = (response) => {
  if (!response || response.status !== 'success' || !Array.isArray(response.plans)) {
    return { bySlug: {}, list: [] };
  }

  const transformed = response.plans.map(transformPlan).filter((p) => p.slug);
  const active = filterActivePlans(transformed);
  const list = sortPlansByOrder(active);
  const bySlug = {};
  list.forEach((plan) => {
    bySlug[plan.slug] = plan;
  });
  return { bySlug, list };
};

async function fetchPlansFromApi({ forceRefresh = false } = {}) {
  // ***** Code Start BH10092026_1716 - Serve warm cache unless forceRefresh
  if (
    !forceRefresh &&
    plansCache &&
    plansCacheTimestamp &&
    Date.now() - plansCacheTimestamp < CACHE_TTL
  ) {
    return plansCache;
  }

  let response;
  try {
    response = await apiService.getSubscriptionPlans();
  } catch (err) {
    // Stale-while-error: keep last good catalog if network blips
    if (plansCache) {
      return plansCache;
    }
    throw err;
  }
  const built = buildFromResponse(response);
  plansCache = built;
  plansCacheTimestamp = Date.now();
  return built;
  // ***** Code End BH10092026_1716
}

export const getAllPlans = async (options = {}) => {
  try {
    const built = await fetchPlansFromApi(options);
    return built.bySlug;
  } catch (error) {
    return {};
  }
};

export const getSortedPlansList = async (options = {}) => {
  try {
    const built = await fetchPlansFromApi(options);
    return built.list || [];
  } catch (error) {
    return [];
  }
};

export const clearPlansCache = () => {
  plansCache = null;
  plansCacheTimestamp = null;
};

const planService = {
  getAllPlans,
  getSortedPlansList,
  clearPlansCache,
};

export default planService;
